import { Dialog, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import RomanianCities from "../../../json/romaniaCities.json";
import { appIcons } from "../../../assets";
import DialogActionButtons from "../../../components/dialog/DialogActions";
import DialogBody from "../../../components/dialog/DialogBody";
import DialogHeader from "../../../components/dialog/DialogHeader";
import InputField from "../../../components/InputField";
import Label from "../../../components/label";
import Grid from "@mui/material/Grid2";
import CustomSelect from "../../../components/select";
import { regiterUserAddressValidation } from "../../../utils/validation";
import { useSnackbar } from "../../../lib/context/snackbarContext";
import { fetchUserAddress } from "./../../../lib/redux/slices/addressSlice";
import {
  createUserAddress,
  updateUserAddress,
} from "../../../services/addressFn";
import { useDispatch, useSelector } from "react-redux";
import AppLoader from "./../../../components/apploader/index";
import { setUserAddress } from "../../../lib/redux/slices/userAddressSlice";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("24px"),
  },
  "& .MuiPaper-root": {
    width: "600px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

const AddEditAddressDialog = forwardRef(({ handleConfirm }, ref) => {
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [inputValues, setInputValues] = useState({
    fname: "",
    lname: "",
    email: "",
    contact: "",
    state: "",
    city: "",
    zipCode: "",
    address: "",
  });
  const [inputError, setInputError] = useState({
    fname: false,
    lname: false,
    email: false,
    isValidEmail: false,
    contact: false,
    state: false,
    city: false,
    zipCode: false,
    address: false,
  });
  const userData = useSelector((state) => state.userReducer.user);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setDialogData(params);
      setOpen(true);
      if (params.type === "edit") {
        setInputValues({
          fname: params?.data?.firstname,
          lname: params?.data?.lastname,
          email: params?.data?.email,
          contact: params?.data?.contact,
          state: params?.data?.state,
          city: params?.data?.city,
          zipCode: params?.data?.zipCode,
          address: params?.data?.address,
        });
      }
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues({});
    setInputError({});
  };

  const handleChangeInputs = (event) => {
    const { name, value } = event.target;

    setInputError((err) => ({
      ...err,
      [name]: false,
      isValidEmail: name === "email" ? false : inputError.isValidEmail,
    }));

    setInputValues((val) => ({ ...val, [name]: value }));
  };

  const handleStateChange = (event) => {
    const selectedState = event.target.value;
    setInputValues((val) => ({ ...val, state: selectedState, city: "" }));
    setInputError((err) => ({ ...err, state: false }));
  };

  const handleCityChange = (event) => {
    const selectedCity = event.target.value;
    setInputValues((val) => ({ ...val, city: selectedCity }));
    setInputError((err) => ({ ...err, city: false }));
  };

  const handleAddEdit = () => {
    const validate = regiterUserAddressValidation(inputValues, setInputError);
    if (validate) return;

    const payload = {
      userId: userData?._id,
      firstname: inputValues?.fname,
      lastname: inputValues?.lname,
      email: inputValues?.email,
      contact: inputValues?.contact,
      state: inputValues?.state,
      city: inputValues?.city,
      zipCode: inputValues?.zipCode,
      address: inputValues?.address,
    };

    if (dialogData?.type === "add") {
      handleApiCall(createUserAddress, payload, "");
    }
    if (dialogData?.type === "edit") {
      handleApiCall(updateUserAddress, payload, dialogData?.data?._id);
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      setIsLoading(true);
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        showSnackbar(response?.data?.message, "success");
        handleClose();
        dialogData?.getData();
        dispatch(fetchUserAddress(userData._id));
        dispatch(setUserAddress(response.data.data));
        localStorage.setItem(
          "user_address",
          JSON.stringify(response.data.data)
        );
      } else {
        showSnackbar(response?.data?.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <DialogHeader
          title={
            dialogData?.type === "add" ? t("addAddress") : t("editAddress")
          }
          secondaryHeading={t("addEditAddress")}
          icon={<img src={appIcons.purpleFeesIcon} width={30} />}
          onClose={handleClose}
        />
        <DialogBody>
          <Grid container rowSpacing={2} columnSpacing={1} my={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label text={t("firstName")} required htmlFor="user-fname" />
              <InputField
                id={"user-fname"}
                required
                inputError={inputError.fname}
                helperText={inputError.fname && t("firstNameRequire")}
                placeholder={`${t("enter")} ${t("firstName")}`}
                type="text"
                name={"fname"}
                value={inputValues.fname}
                onChange={handleChangeInputs}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label text={t("lastName")} required htmlFor="user-lname" />
              <InputField
                id={"user-lname"}
                required
                inputError={inputError.lname}
                helperText={inputError.lname && t("lastNameRequire")}
                placeholder={`${t("enter")} ${t("lastName")}`}
                type="text"
                name={"lname"}
                value={inputValues.lname}
                onChange={handleChangeInputs}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label text={t("emailAddress")} required htmlFor="user-email" />
              <InputField
                id={"user-email"}
                required
                inputError={inputError.email || inputError.isValidEmail}
                helperText={
                  inputError.email
                    ? t("emailAddRequired")
                    : inputError.isValidEmail
                    ? t("invalidEmailFormat")
                    : null
                }
                placeholder={`${t("enter")} ${t("emailAddress")}`}
                type="email"
                name={"email"}
                value={inputValues.email}
                onChange={handleChangeInputs}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label text={t("contact")} required htmlFor="user-contact" />
              <InputField
                id={"user-contact"}
                required
                inputError={inputError.contact}
                helperText={inputError.contact && t("contactRequired")}
                placeholder={`${t("enter")} ${t("contactNumber")}`}
                type="number"
                name={"contact"}
                value={inputValues.contact}
                onChange={handleChangeInputs}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label text={t("country")} required htmlFor="user-country" />
              <InputField
                id={"user-country"}
                required
                readonly
                value={"Romania"}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label text={t("state")} required htmlFor="user-state" />
              <CustomSelect
                id="user-state"
                name="state"
                value={inputValues.state}
                onChange={handleStateChange}
                selectError={inputError.state}
                helperText={inputError.state && t("stateRequired")}
                formStyleProps={{
                  "& .MuiSelect-outlined": { padding: "8px !important" },
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      overflowY: "auto", // make sure the menu scrolls
                    },
                  },
                }}
              >
                <MenuItem value="">{t("selectState")}</MenuItem>
                {RomanianCities.map((stateObj) => (
                  <MenuItem key={stateObj.state} value={stateObj.state}>
                    {stateObj.state}
                  </MenuItem>
                ))}
              </CustomSelect>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label text={t("city")} required htmlFor="user-city" />
              <CustomSelect
                id="user-city"
                name="city"
                value={inputValues.city}
                onChange={handleCityChange}
                selectError={inputError.city}
                helperText={inputError.city && t("cityRequired")}
                //   disabled={!inputValues.state}
                formStyleProps={{
                  "& .MuiSelect-outlined": { padding: "8px !important" },
                }}
              >
                <MenuItem value="">{t("selectCity")}</MenuItem>
                {inputValues.state &&
                  RomanianCities.find(
                    (stateObj) => stateObj.state === inputValues.state
                  )?.cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
              </CustomSelect>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label text={t("zipCode")} required htmlFor="user-zip-code" />
              <InputField
                id={"user-zip-code"}
                required
                inputError={inputError.zipCode}
                helperText={inputError.zipCode && t("zipCodeRequired")}
                placeholder={`${t("enter")} ${t("zipCode")}`}
                type="text"
                name={"zipCode"}
                value={inputValues.zipCode}
                onChange={handleChangeInputs}
              />
            </Grid>
            <Grid size={12}>
              <Label text={t("address")} required htmlFor="user-address" />
              <InputField
                id={"user-address"}
                required
                inputError={inputError.address}
                helperText={inputError.address && t("addressRequired")}
                placeholder={`${t("enter")} ${t("fullAddress")}`}
                type="text"
                name={"address"}
                value={inputValues.address}
                onChange={handleChangeInputs}
              />
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          confirmText={dialogData?.type === "add" ? t("add") : t("edit")}
          onCancel={handleClose}
          onConfirm={handleAddEdit}
        />
      </BootstrapDialog>
    </>
  );
});

export default AddEditAddressDialog;
